import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, UserCog } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Field = ({ label, hint, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
    {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
  </div>
);

/**
 * Form "Akun Saya" — dipakai di dasbor superadmin/admin/teknisi dan portal mitra/user.
 * Ubah username & password wajib memasukkan password lama. Setelah username berubah,
 * sesi otomatis diakhiri dan user diarahkan ke halaman login.
 */
export default function AccountProfileForm({ className = "" }) {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ username: "", name: "", phone: "", current_password: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user !== true && user.username) {
      setF((c) => ({ ...c, username: user.username || "", name: user.name || "", phone: user.phone || "" }));
    }
  }, [user]);

  const currentUsername = (user && user !== true && user.username) || "";
  const nextUsername = f.username.trim().toLowerCase();
  const usernameChanged = !!nextUsername && nextUsername !== currentUsername;
  const wantsPassword = !!f.password;
  const needsCurrent = usernameChanged || wantsPassword;

  const submit = async (e) => {
    e.preventDefault();
    if (!nextUsername) return toast.error("Username tidak boleh kosong");
    if (usernameChanged && !/^[a-z0-9._-]{3,20}$/.test(nextUsername)) {
      return toast.error("Username 3-20 karakter: huruf kecil, angka, titik, garis bawah, atau strip");
    }
    if (wantsPassword && f.password.length < 6) return toast.error("Password baru minimal 6 karakter");
    if (wantsPassword && f.password !== f.confirm) return toast.error("Konfirmasi password tidak sama");
    if (needsCurrent && !f.current_password) {
      return toast.error("Masukkan password lama untuk mengubah username atau password");
    }
    setBusy(true);
    try {
      const body = { name: f.name, phone: f.phone };
      if (usernameChanged) body.username = nextUsername;
      if (wantsPassword) body.password = f.password;
      if (needsCurrent) body.current_password = f.current_password;
      const { data } = await api.put("/auth/profile", body);
      setF((c) => ({ ...c, current_password: "", password: "", confirm: "" }));
      if (data.username_changed) {
        toast.success(`Username diubah menjadi "${data.username}". Silakan login ulang.`);
        await logout();
        nav("/login", { replace: true });
        return;
      }
      toast.success(data.password_changed ? "Profil & password diperbarui" : "Profil diperbarui");
      refresh();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={`space-y-5 rounded-2xl border border-border bg-card p-6 ${className}`}
      data-testid="account-profile-form"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-600/20 text-violet-400">
          <UserCog className="h-5 w-5" />
        </span>
        <div>
          <h3 className="font-display text-lg font-semibold">Akun Saya</h3>
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{(user && user.role) || ""}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Username"
          value={f.username}
          onChange={(e) => setF({ ...f, username: e.target.value })}
          data-testid="acct-username"
          hint="Huruf kecil, angka, titik, garis bawah, atau strip (3-20 karakter)"
        />
        <Field label="Nama" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="acct-name" />
        <Field label="No. WhatsApp" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="acct-phone" />
      </div>

      <div className="space-y-4 rounded-xl border border-dashed border-border p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <KeyRound className="h-4 w-4 text-cyan-400" /> Ganti Password (opsional)
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Password Baru"
            type="password"
            autoComplete="new-password"
            value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })}
            data-testid="acct-new-password"
            hint="Minimal 6 karakter. Biarkan kosong bila tidak diganti."
          />
          <Field
            label="Ulangi Password Baru"
            type="password"
            autoComplete="new-password"
            value={f.confirm}
            onChange={(e) => setF({ ...f, confirm: e.target.value })}
            data-testid="acct-confirm-password"
          />
        </div>
      </div>

      {needsCurrent && (
        <div className="space-y-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="acct-current-block">
          <div className="flex items-start gap-2 text-xs text-amber-400">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {usernameChanged
                ? "Mengubah username memerlukan password lama. Setelah tersimpan, Anda akan otomatis keluar dan harus login memakai username baru."
                : "Mengubah password memerlukan password lama Anda."}
            </span>
          </div>
          <Field
            label="Password Lama"
            type="password"
            autoComplete="current-password"
            value={f.current_password}
            onChange={(e) => setF({ ...f, current_password: e.target.value })}
            data-testid="acct-current-password"
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={busy}
        data-testid="acct-save"
        className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white sm:w-auto"
      >
        {busy ? "Menyimpan…" : "Simpan Perubahan"}
      </Button>
    </form>
  );
}
